import { parseFloatSafe } from "@omni/utils/number.utils";
import queryString from "query-string";
import { PureComponent } from "react";

import * as CalculatorStepTracker from "@/services/tracking/trackers/tools/calculators/calculatorStep.tracker";
import { CALCULATOR_KEYS } from "@/services/tracking/trackers/tools/calculators/config";
import { asyncState, isValidPositiveNumber } from "@/utils/calculators";

import Calculation from "./calculation/calculation.component";
import Result from "./result/result.component";

type GlassBrickCalculatorState = {
    wallLength: string;
    wallHeight: string;
    glassBrickWidth: string;
    glassBrickLength: string;
    glassBrickHeight: string;
    wallArea: number;
    glassBrickArea: number;
    numberOfGlassBricks: number;
    numberOfGlassBrickCrosses: number;
    mortarVolume: number;
    jointCementVolume: number;
};

class GlassBrickCalculator extends PureComponent<
    {},
    GlassBrickCalculatorState
> {
    query = queryString.parse(location.search);

    constructor(props: {}) {
        super(props);

        this.state = {
            // calculationProps
            wallLength: this.query.wallLength?.toString() || "0",
            wallHeight: this.query.wallHeight?.toString() || "0",
            glassBrickWidth: this.query.glassBrickWidth?.toString() || "0",
            glassBrickLength: this.query.glassBrickLength?.toString() || "0",
            glassBrickHeight: this.query.glassBrickHeight?.toString() || "0",

            // resultProps
            wallArea: 0,
            glassBrickArea: 0,
            numberOfGlassBricks: 0,
            numberOfGlassBrickCrosses: 0,
            mortarVolume: 0,
            jointCementVolume: 0
        };
    }

    async componentDidMount() {
        await this.calculateFields(true);
    }

    // calculations
    wallArea = () => {
        return (
            parseFloatSafe(this.state.wallLength, 0) *
            parseFloatSafe(this.state.wallHeight, 0)
        );
    };
    glassBrickArea = () => {
        let glassBrickArea = 0;

        const glassBrickLength = parseFloatSafe(this.state.glassBrickLength, 0);

        const glassBrickHeight = parseFloatSafe(this.state.glassBrickHeight, 0);

        if (
            isValidPositiveNumber(glassBrickLength) &&
            isValidPositiveNumber(glassBrickHeight)
        ) {
            glassBrickArea =
                (glassBrickLength + 1) * (glassBrickHeight + 1) * 0.0001;
        }

        return glassBrickArea;
    };
    numberOfGlassBricks = () => {
        let numberOfGlassBricks = 0;

        const wallArea = this.state.wallArea;

        const glassBrickArea = this.state.glassBrickArea;

        if (
            isValidPositiveNumber(wallArea) &&
            isValidPositiveNumber(glassBrickArea)
        ) {
            numberOfGlassBricks = Number(Math.ceil(wallArea / glassBrickArea));
        }

        return numberOfGlassBricks;
    };
    numberOfGlassBrickCrosses = () => {
        const wallHeight = parseFloatSafe(this.state.wallHeight, 0);

        const brickHeight =
            parseFloatSafe(this.state.glassBrickHeight, 0) * 0.01;
        const numberOfGlassbricksHigh = Math.ceil(wallHeight / brickHeight);

        const wallLength = parseFloatSafe(this.state.wallLength, 0);

        const brickLength =
            parseFloatSafe(this.state.glassBrickLength, 0) * 0.01;
        const numberOfGlassbricksLong = Math.ceil(wallLength / brickLength);

        const numberOfGlassbrickCrosses =
            (numberOfGlassbricksHigh + 1) * (numberOfGlassbricksLong + 1);

        return isValidPositiveNumber(numberOfGlassbrickCrosses)
            ? numberOfGlassbrickCrosses
            : 0;
    };
    mortarVolume = () => {
        const brickLength = parseFloatSafe(this.state.glassBrickLength, 0);

        const brickWidth = parseFloatSafe(this.state.glassBrickWidth, 0);

        const brickHeight = parseFloatSafe(this.state.glassBrickHeight, 0);

        const brickVolume =
            brickLength * brickWidth * brickHeight * 0.001 * 0.001;

        const volumeTotalBricks = brickVolume * this.state.numberOfGlassBricks;

        const wallVolume = this.state.wallArea * brickWidth * 0.01;

        return wallVolume - volumeTotalBricks;
    };
    joinCementVolume = () => {
        const areaJointCement = 0.01 * 0.01;

        const numberOfBricks = this.state.numberOfGlassBricks;

        const brickHeight =
            parseFloatSafe(this.state.glassBrickHeight, 0) / 100;

        const brickLength =
            parseFloatSafe(this.state.glassBrickLength, 0) / 100;
        const brickDeadSpace = areaJointCement * 0.01;

        return (
            (brickLength * numberOfBricks * areaJointCement +
                brickHeight * numberOfBricks * areaJointCement +
                brickDeadSpace * numberOfBricks) *
            2
        );
    };

    // setters
    setBrickMeasurements = (length: string, width: string, height: string) => {
        this.setState(
            {
                glassBrickLength: length,
                glassBrickWidth: width,
                glassBrickHeight: height
            },
            () => this.calculateFields()
        );
    };
    setCalculationValue = (field: string, value: string) => {
        this.setState(
            (state) => ({ ...state, [field]: value }),
            async () => {
                await this.calculateFields();
            }
        );
    };
    calculateFields = async (initialCall?: boolean) => {
        await asyncState(this, {
            wallArea: this.wallArea(),
            glassBrickArea: this.glassBrickArea(),
            numberOfGlassBrickCrosses: this.numberOfGlassBrickCrosses()
        });
        await asyncState(this, {
            numberOfGlassBricks: this.numberOfGlassBricks()
        });
        await asyncState(this, {
            mortarVolume: this.mortarVolume(),
            jointCementVolume: this.joinCementVolume()
        });
        if (initialCall) {
            CalculatorStepTracker.trackStart(CALCULATOR_KEYS.glassBrick);
        } else {
            CalculatorStepTracker.trackSubmit(CALCULATOR_KEYS.glassBrick);
        }
    };

    render() {
        const calculationProps = {
            setCalculationValue: this.setCalculationValue,
            setBrickMeasurements: this.setBrickMeasurements,
            wallLength: this.state.wallLength,
            wallHeight: this.state.wallHeight,
            glassBrickLength: this.state.glassBrickLength,
            glassBrickWidth: this.state.glassBrickWidth,
            glassBrickHeight: this.state.glassBrickHeight
        };
        const resultsProps = {
            wallArea: this.state.wallArea,
            glassBrickArea: this.state.glassBrickArea,
            numberOfGlassBricks: this.state.numberOfGlassBricks,
            numberOfGlassBrickCrosses: this.state.numberOfGlassBrickCrosses,
            mortarVolume: this.state.mortarVolume,
            jointCementVolume: this.state.jointCementVolume,
            mailParams: {
                wallLength: this.state.wallLength,
                wallHeight: this.state.wallHeight,
                glassBrickLength: this.state.glassBrickLength,
                glassBrickWidth: this.state.glassBrickWidth,
                glassBrickHeight: this.state.glassBrickHeight
            }
        };

        return (
            <div id="calculator">
                <Calculation {...calculationProps} />
                <Result {...resultsProps} />
            </div>
        );
    }
}

export { GlassBrickCalculator };
